import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { type AppRole, pickEffectiveRole } from "@/lib/roles";
import { STALE } from "@/lib/queryClient";
import { clearStepUp } from "@/lib/stepup";
import { registerSecuritySession } from "@/lib/security/session";

interface AuthCtx {
  session: Session | null;
  user: User | null;
  role: AppRole | null;
  roles: AppRole[];
  organizationId: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}


const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const userId = session?.user?.id;

  const { data: roleRows, isPending: rolesPending } = useQuery({
    queryKey: ["user_role", userId],
    queryFn: async () => {
      if (!userId) return [] as AppRole[];
      const { data, error } = await supabase.from("user_roles").select("role").eq("user_id", userId);
      if (error) throw error;
      return (data ?? []).map((r) => r.role as AppRole);
    },
    enabled: !!userId,
    staleTime: STALE.static,
  });



  const { data: profileRow } = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase.from("profiles").select("organization_id").eq("id", userId).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    staleTime: STALE.static,
  });

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      if (!sess?.user) {
        qc.removeQueries({ queryKey: ["user_role"] });
        qc.removeQueries({ queryKey: ["profile"] });
        clearStepUp();
      } else {
        // SH-5: register/refresh the server-side security session + device record.
        setTimeout(() => { void registerSecuritySession(); }, 0);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
      if (data.session) void registerSecuritySession();
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  const refresh = async () => {
    if (!userId) return;
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["user_role", userId] }),
      qc.invalidateQueries({ queryKey: ["profile", userId] }),
    ]);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    qc.clear();
  };

  return (
    <Ctx.Provider
      value={{
        session,
        user: session?.user ?? null,
        role: pickEffectiveRole(roleRows),
        roles: roleRows ?? [],

        organizationId: profileRow?.organization_id ?? null,
        loading: loading || (!!userId && rolesPending),

        refresh,
        signOut,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
};
